'use client'

import React, { useEffect, useRef, useState } from 'react'

/** Design: 6 s on screen, then fade. */
const TOAST_VISIBLE_MS = 6000
/** Matches the CSS transition so the node leaves after it has faded out. */
const TOAST_FADE_MS = 240

/**
 * Ink-black success toast shown after the OTP dialog closes.
 *
 * It is not redundant with the dialog's tick: the visitor's eyes are on the
 * dialog while the section swaps behind it, so this is the only confirmation
 * of the change they did not watch happen. Deliberately ink-black, not green
 * (brand-spec: success stays in the mono system).
 */
export function CampaignToast({
  open,
  message,
  onClose,
}: {
  open: boolean
  message: string
  onClose: () => void
}) {
  const [visible, setVisible] = useState(false)
  // Held in a ref so an inline `onClose={() => …}` from the parent cannot
  // retrigger the effect: re-arming the timer on every parent re-render would
  // keep the toast on screen well past its 6 seconds.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) {
      setVisible(false)
      return
    }
    // Mount transparent, then flip on the next frame so the CSS transition has
    // two states to move between.
    const raf = requestAnimationFrame(() => setVisible(true))
    const hide = window.setTimeout(() => setVisible(false), TOAST_VISIBLE_MS)
    const unmount = window.setTimeout(() => onCloseRef.current(), TOAST_VISIBLE_MS + TOAST_FADE_MS)
    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(hide)
      window.clearTimeout(unmount)
    }
  }, [open])

  if (!open || !message) return null

  return (
    <div className={visible ? 'camp-toast show' : 'camp-toast'} role="status">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" />
      </svg>
      <span>{message}</span>
    </div>
  )
}
